import { useState } from 'react';

import { Box, Paper, Typography, Button, useTheme, useMediaQuery } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';

import { resolvePhoneNumberFormat } from '@/utils';

import { mobileStyles, TeletriagePaperStyles } from './Teletriage.styles';

export interface TeletriageType {
	/**
	 * Phone number to display on the cta.
	 **/
	phoneNumber: string;
}

export default function Teletriage({ phoneNumber }: TeletriageType) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const [expanded, setExpanded] = useState(false);

	return (
		<Paper
			elevation={4}
			sx={TeletriagePaperStyles({ isMobile, expanded })}
			onClick={() => setExpanded(!expanded)}
			data-testid="add-appointment-box"
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					...(isMobile && !expanded && { width: '32px', margin: '0 auto' }),
				}}
			>
				<Box sx={{ height: '32px', width: '32px' }}>
					<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
						<path
							d="M3.40201 11.9555C2.898 9.70377 3.79466 7.35203 5.7878 6.20129C8.27931 4.76304 9.28445 7.04874 10.8149 9.69954C12.3453 12.3502 13.8215 14.3626 11.33 15.8011C10.5862 16.2306 9.77383 16.4426 8.95942 16.4606C9.79251 18.0861 10.8233 19.3796 11.9481 20.5043C13.016 21.5723 14.1915 22.5097 15.6438 23.2596C15.6443 22.4087 15.8587 21.557 16.3074 20.7798C17.7459 18.2882 19.7582 19.7645 22.409 21.295C25.0598 22.8254 27.3456 23.8305 25.9072 26.3221C24.6825 28.4432 22.0977 29.3226 19.7207 28.5932C14.1931 27.2661 11.4525 25.2635 9.14034 22.9516C6.8224 20.6369 4.98149 18.0353 3.50351 12.3498C3.46785 12.2306 3.43725 12.1107 3.4096 11.9907C3.40686 11.9798 3.40406 11.9685 3.40132 11.9576L3.40201 11.9555Z"
							fill="#61B3E4"
						/>
						<path
							d="M19.7087 12.3978C20.3025 12.9915 20.6314 13.6925 20.8115 14.2118C20.9032 14.4762 20.9609 14.7075 20.9965 14.8761C21.0144 14.9605 21.0269 15.0303 21.0352 15.0819C21.0393 15.1074 21.0426 15.1291 21.0449 15.1454C21.046 15.1536 21.0469 15.1618 21.0476 15.1675C21.048 15.1701 21.0488 15.1723 21.049 15.1744V15.1799L21.0559 15.2821C21.0652 15.789 20.6896 16.2307 20.1755 16.2924C19.6279 16.358 19.1312 15.9674 19.0644 15.4202C19.0639 15.4165 19.0624 15.4103 19.061 15.4016C19.0573 15.3789 19.0503 15.3398 19.0396 15.289C19.018 15.1868 18.9809 15.0384 18.9215 14.8671C18.7991 14.5141 18.6002 14.1176 18.2945 13.812C17.9889 13.5065 17.5929 13.308 17.24 13.1857C17.0684 13.1262 16.9197 13.0885 16.8174 13.0669C16.7667 13.0562 16.7283 13.0498 16.7056 13.0462C16.6964 13.0447 16.69 13.0426 16.6862 13.042C16.1393 12.9751 15.7493 12.4791 15.8148 11.9316C15.8805 11.3833 16.3782 10.9918 16.9265 11.0574H16.9279H16.9293L16.9397 11.0595C16.9454 11.0602 16.9529 11.0604 16.9611 11.0616C16.9775 11.0639 16.9989 11.0671 17.0246 11.0712C17.0761 11.0796 17.1459 11.0921 17.2304 11.1099C17.399 11.1455 17.6308 11.204 17.8954 11.2957C18.4146 11.4757 19.1151 11.8042 19.7087 12.3978ZM19.0644 15.4244L19.0637 15.4223L19.0644 15.4202V15.4244ZM22.7243 14.8056L22.725 14.8077C22.7248 14.8069 22.7243 14.805 22.7236 14.8008C22.7219 14.7918 22.7188 14.7749 22.7139 14.7511C22.7041 14.7031 22.6867 14.6265 22.6607 14.5267C22.6087 14.3266 22.5212 14.0347 22.3811 13.6891C22.0996 12.9948 21.6162 12.1038 20.8094 11.297C20.0026 10.4903 19.1115 10.0071 18.4174 9.72539C18.0719 9.58521 17.7804 9.49708 17.5805 9.44503C17.4807 9.41906 17.404 9.40168 17.3561 9.39186C17.3322 9.38699 17.3154 9.38382 17.3063 9.3822C17.3046 9.38189 17.3033 9.38101 17.3022 9.38081C16.7572 9.29465 16.3852 8.78393 16.4708 8.23867C16.5566 7.69338 17.0676 7.3205 17.6129 7.40589L17.4873 8.20553C17.6129 7.40605 17.614 7.40584 17.6143 7.40589L17.6164 7.40658C17.6174 7.40674 17.6186 7.40707 17.6198 7.40727C17.6227 7.40773 17.6269 7.40799 17.6309 7.40865C17.6386 7.40995 17.6482 7.41208 17.6599 7.41417C17.6841 7.41852 17.7172 7.42463 17.7573 7.43282C17.8372 7.44919 17.9478 7.47406 18.0839 7.50947C18.3558 7.58025 18.7324 7.69437 19.1701 7.872C20.0426 8.22611 21.1812 8.84041 22.2236 9.88283C23.2663 10.9255 23.8805 12.0649 24.2345 12.9378C24.412 13.3755 24.5263 13.752 24.597 14.024C24.6323 14.16 24.6573 14.2706 24.6736 14.3506C24.6818 14.3906 24.6873 14.4231 24.6916 14.4473C24.6938 14.4594 24.6958 14.4698 24.6971 14.4776C24.6977 14.4813 24.6981 14.4846 24.6985 14.4873C24.6987 14.4887 24.6997 14.4903 24.6999 14.4915V14.4942L24.7109 14.5964C24.7383 15.1025 24.3779 15.5569 23.8664 15.6371C23.3548 15.717 22.8734 15.3942 22.745 14.9037L22.7243 14.8056ZM24.7379 7.3686C26.2664 8.89712 27.1727 10.4985 27.6975 11.7217C27.9601 12.3337 28.1282 12.8538 28.232 13.2271C28.2839 13.4138 28.3203 13.5643 28.3438 13.6718C28.3556 13.7255 28.364 13.7687 28.3701 13.8002C28.3731 13.816 28.3752 13.829 28.377 13.8389C28.3779 13.8438 28.3791 13.8479 28.3797 13.8513C28.38 13.853 28.3802 13.8548 28.3804 13.8562V13.8589C28.3768 13.8599 28.3246 13.8683 27.5953 13.9908L28.3804 13.8589C28.4717 14.4035 28.1046 14.9193 27.5601 15.0107C27.0165 15.102 26.5019 14.7367 26.409 14.1938C26.4086 14.1917 26.4076 14.1861 26.4062 14.1786C26.4032 14.1632 26.3977 14.1367 26.3896 14.0999C26.3735 14.0264 26.3468 13.9118 26.3054 13.7629C26.2225 13.4648 26.0824 13.0303 25.8593 12.5103C25.4127 11.4694 24.6368 10.096 23.3236 8.78281C22.0105 7.4697 20.637 6.69374 19.5961 6.24717C19.0765 6.02424 18.6423 5.88466 18.3442 5.80178C18.1953 5.76037 18.0801 5.73295 18.0065 5.71684C17.9702 5.70889 17.9439 5.70393 17.9285 5.70096C17.9225 5.69979 17.918 5.69798 17.9154 5.69751C17.3712 5.60564 17.0043 5.09076 17.0957 4.54639C17.1873 4.00182 17.7029 3.63456 18.2475 3.72603L18.1419 4.35649C18.2377 3.7867 18.2464 3.73125 18.2475 3.72603H18.2489L18.251 3.72672C18.2523 3.72694 18.2542 3.72713 18.2558 3.72741C18.2591 3.72798 18.263 3.72866 18.2676 3.72948C18.2774 3.73125 18.2906 3.73338 18.3062 3.73639C18.3377 3.74245 18.3816 3.75155 18.4354 3.76332C18.5428 3.78685 18.693 3.82269 18.8794 3.8745C19.2526 3.97826 19.7728 4.14645 20.3847 4.40897C21.6079 4.93377 23.2094 5.84015 24.7379 7.3686Z"
							fill="#6DAA63"
						/>
					</svg>
				</Box>
				<Typography
					variant="bodyMediumBold"
					color="text.primary"
					ml={0.5}
					sx={{
						...(isMobile && mobileStyles(expanded)),
					}}
					aria-expanded={expanded ? 'true' : 'false'}
				>
					Teletriage now available
				</Typography>
				<Box
					sx={{
						display: 'flex',
						marginLeft: 'auto',
						...(isMobile && mobileStyles(expanded)),
					}}
				>
					{expanded ? (
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
							<path d="M6 11H18V13H6V11Z" fill="#184FB4" />
						</svg>
					) : (
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
							<path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" fill="#184FB4" />
						</svg>
					)}
				</Box>
			</Box>
			<Box
				sx={{
					maxHeight: expanded ? '110px' : '0px',
					px: '36px',
					transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
				}}
			>
				<Box
					sx={{
						opacity: expanded ? 1 : 0,
						transform: expanded ? 'translateY(0)' : 'translateY(-10px)',
						transition: `opacity 0.3s ease-in-out 0.4s, transform 0.3s ease-in-out 0.4s`,
					}}
				>
					<Typography variant="bodySmallBook" color="text.tertiary">
						Our easy teletriage service is now available for your pets! Get quick advice from licensed
						veterinary technicians.
					</Typography>
				</Box>
			</Box>
			<Box
				sx={{
					maxHeight: expanded ? '110px' : '0px',
					opacity: expanded ? 1 : 0,
					transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out 0.3s',
				}}
			>
				<Button
					variant="primaryDefault"
					size="M"
					fullWidth
					sx={{ marginTop: '20px' }}
					href={`tel:+1${phoneNumber}`}
					onClick={(event) => {
						event.stopPropagation();
					}}
				>
					<PhoneIcon sx={{ width: '20px', height: '20px', marginRight: '4px' }} />
					{resolvePhoneNumberFormat(phoneNumber || '')}
				</Button>
			</Box>
		</Paper>
	);
}
